import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { mapboxForwardGeocode, type MapboxFeature } from '@/lib/mapbox';

/** Mapbox-backed address typeahead.
 *
 *  Owns its debounce + suggestion fetch. The parent supplies the visible
 *  text value; on suggestion select the parent receives the structured
 *  `MapboxFeature` and decides how to split it across its own fields.
 *
 *  Geocoding is constrained to New Zealand at the helper level — see
 *  `GEOCODE_COUNTRIES` in `lib/mapbox.ts`. */
export function AddressAutocomplete({
  value, onChange, onSelect, placeholder, ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (feature: MapboxFeature) => void;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [debounced, setDebounced] = React.useState(value);
  const [highlight, setHighlight] = React.useState(0);

  // Trail typed text by ~250ms so we don't fire one request per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  // Skip single-letter prefixes — Mapbox returns mostly-noise for those
  // and they spam the API.
  const { data: suggestions = [] } = useQuery<MapboxFeature[]>({
    queryKey: ['mapbox-autocomplete', debounced],
    queryFn: () => mapboxForwardGeocode(debounced, { autocomplete: true, limit: 5 }),
    enabled: open && debounced.trim().length >= 3,
    staleTime: 60_000,
  });

  function pick(f: MapboxFeature) {
    onSelect(f);
    setOpen(false);
  }

  return (
    <div className="relative">
      <Input
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        // Delay close so the suggestion's mousedown lands before blur
        // tears the dropdown down.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || suggestions.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(suggestions[highlight]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border bg-popover shadow-md text-sm"
        >
          {suggestions.map((f, i) => (
            <li
              key={`${f.place_name}-${i}`}
              role="option"
              aria-selected={i === highlight}
              // mousedown fires before the input's blur — picks the
              // suggestion before the dropdown closes.
              onMouseDown={(e) => { e.preventDefault(); pick(f); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-2 cursor-pointer ${i === highlight ? 'bg-accent text-accent-foreground' : ''}`}
            >
              <div className="truncate">{f.text}</div>
              <div className="truncate text-xs text-muted-foreground">{f.place_name}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
